import { ReactNode } from 'react'
import { Badge, Menu } from '@systemeio/components'
import { DotsHorizontalIcon } from '@systemeio/icons'
import { cn } from 'cn'
import { useTranslation } from 'next-i18next/pages'
import { useFormatDateTime } from 'shared/hooks/use-format-date-time'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { notificationIcons } from 'modules/profile/notifications/constants/notification-icons'
import { useNotificationsActions } from 'modules/profile/notifications/hooks/use-notification-actions'
import { NotificationItemInterface } from 'modules/profile/notifications/types/notification-types'

interface NotificationActionProps {
  label: ReactNode
  onClick: () => void
  dataTestAttributes?: Record<string, string>
}

export interface NotificationItemProps {
  notification: NotificationItemInterface
  actions?: NotificationActionProps[]
}

const NotificationItem = ({ notification, actions }: NotificationItemProps): ReactNode => {
  const { user } = useUser()
  const { t } = useTranslation()
  const { id, eventType, message, read, createdAt } = notification
  const createdAtFormatted = useFormatDateTime(createdAt)
  const { getNotificationLink } = useNotificationsActions()

  const Icon = notificationIcons[eventType]

  const handleNotificationClick = async (): Promise<void> => {
    const notificationLinkTab = window.open('', '_blank')
    const { link } = await getNotificationLink(id)
    notificationLinkTab!.location.href = link
  }

  return (
    <div
      className={cn(
        `group flex flex-row justify-between border-b border-text-secondary/10 bg-bg-primary px-6 py-4 text-text-primary transition last:border-b-0 hover:cursor-pointer hover:bg-bg-accent/10`,
        read && `text-text-secondary group-hover:text-text-primary`,
      )}
    >
      <div
        className="inline-flex min-w-0 flex-row items-center gap-4"
        onClick={handleNotificationClick}
      >
        {Icon && (
          <Icon
            className={`size-5 shrink-0 fill-text-tertiary text-text-tertiary transition group-hover:fill-text-primary group-hover:text-text-primary`}
          />
        )}
        <div className="min-w-0 pr-3">
          <div>
            <span
              className="wrap-break-word transition group-hover:text-text-primary"
              dangerouslySetInnerHTML={{ __html: message }}
            />
          </div>
          {isAuthorizedUser(user) && (
            <div className="transition group-hover:text-text-primary">
              {createdAtFormatted?.formatted}
            </div>
          )}
        </div>
      </div>
      <div className="inline-flex flex-row items-center gap-4">
        {!read && (
          <Badge color="outlinedGray" size="sm" shape="pill">
            {t('profile.user_settings.notifications.new')}
          </Badge>
        )}
        {actions && actions.length > 0 && (
          <Menu
            trigger={<DotsHorizontalIcon className="size-5" />}
            items={actions.map((action, index) => ({
              id:
                typeof action.label === 'string'
                  ? `${action.label}-na-action`
                  : `notification-action-${index}`,
              label: action.label,
              onSelect: action.onClick,
              dataAttributes: action.dataTestAttributes as
                | Record<`data-${string}`, string>
                | undefined,
            }))}
          />
        )}
      </div>
    </div>
  )
}

export default NotificationItem
